'use client';

import { XMarkIcon } from '@heroicons/react/24/solid';
import { clsx } from 'clsx';
import { addTranslation, setLanguage } from 'media-chrome/dist/utils/i18n.js';
import { useLocale } from 'next-intl';
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Button, HeroIcon } from '@/components/atoms';
import { Sheet } from '@/components/molecules';
import { Toast, ToastImperativeHandle } from '@/components/molecules/Toast';
import { ExpandedPlayer } from '@/components/organisms/ExpandedPlayer';
import MiniPlayer, {
  MiniPlayerHandles,
} from '@/components/organisms/MiniPlayer/MiniPlayer';
import { AddMenu } from '@/components/organisms/TrackList/TrackListComponents/AddMenu';
import { useAuthContext } from '@/lib/contexts/auth/auth-provider';
import { useCanAccess, useStreamingPreferences } from '@/lib/hooks/auth';
import { useDialog } from '@/lib/hooks/auth/useDialog';
import { logger } from '@/lib/logger';
import { AccessContentPayload } from '@/lib/utils/access';
import { Duration } from '@/lib/utils/time-formatting';

import { Translations } from './translations';
import {
  Compilation,
  CompilationNormalized,
  CompilationType,
  normalizeAsset,
  normalizeCompilation,
  TrackNormalized,
} from './util';

import type { TrackFileType, SiteConfigResponse } from '@/lib/api';

import type { UUID } from '@/types';

// Import default media-chrome translations
// https://www.media-chrome.org/docs/en/internationalization/adding-language-support#using-javascript
import 'media-chrome/lang/en.js';
import 'media-chrome/lang/fr.js';
import 'media-chrome/lang/es.js';

interface PlaylistTrack {
  playlistIndex: number;
  trackIndex: number;
}

type QueueUpCompilationFunction = (
  key: UUID,
  compilation: Compilation,
  startTrackIndex?: number,
) => void;

interface PlayQueueItem {
  key: UUID;
  data: CompilationNormalized;
}

interface MediaControllerState {
  queueUpCompilation: QueueUpCompilationFunction;
  clearOutQueue: () => void;
  pauseMiniPlayer: (hideMiniPlayer?: boolean) => void;
  resumeMiniPlayer: (showMiniPlayer?: boolean) => void;
  currentCompilationId?: UUID;
  isPlaying: boolean;
  currentTrackId?: string;
  currentCompilationType?: CompilationType;
  trackIndex?: number;
  openAddMenu: (trackId: number) => void;
  closeAddMenu: () => void;
}

export const MediaControllerContext = createContext({} as MediaControllerState);

type Props = PropsWithChildren;
interface MediaControllerProviderProps extends Props {
  config: Awaited<ReturnType<typeof import('@/lib/api').fetchSiteConfig>>;
}

export function MediaControllerProvider(props: MediaControllerProviderProps) {
  const { children, config } = props;

  const [siteConfigData, setSiteConfigData] = useState<SiteConfigResponse>();
  const [showMiniPlayer, setShowMiniPlayer] = useState<boolean>();
  const [miniPlayerHeight, setMiniPlayerHeight] = useState<number>();
  const [playlistTrack, setPlaylistTrack] = useState<PlaylistTrack>();
  const [miniPlayerDuration, setMiniPlayerDuration] = useState<Duration>();
  const [miniPlayerSelectedFormat, setMiniPlayerSelectedFormat] =
    useState<TrackFileType>();
  const [miniPlayerAvailableFormats, setMiniPlayerAvailableFormats] = useState<
    TrackFileType[]
  >([]);
  const [showExpandedPlayer, setShowExpandedPlayer] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const miniPlayerWrapperRef = useRef<HTMLDivElement>(null);
  const miniPlayerRef = useRef<MiniPlayerHandles>(null);
  const miniPlayerObserverRef = useRef<ResizeObserver>(null);
  const miniPlayerExplicityPaused = useRef<boolean>(false);
  const playQueue = useRef<PlayQueueItem[]>([]);
  const pendingLoadRef = useRef<{
    compilation: CompilationNormalized;
    track: TrackNormalized;
    format: TrackFileType | undefined;
  } | null>(null);
  const lastGrantedPlaylistTrackRef = useRef<PlaylistTrack | undefined>(
    undefined,
  );
  const blockedQueueEntryRef = useRef<{
    entry: PlayQueueItem;
    trackIndex: number;
  } | null>(null);
  const toastRef = useRef<ToastImperativeHandle>(null);
  const [addMenuTrackId, setAddMenuTrackId] = useState<number | null>(null);

  const locale = useLocale();
  const { isAuthenticated } = useAuthContext();
  const { openDialog } = useDialog();
  const { getStreamingPreference, setStreamingPreference } =
    useStreamingPreferences();

  const handleResize = () => {
    if (!miniPlayerWrapperRef.current) return;

    setMiniPlayerHeight(miniPlayerWrapperRef.current.clientHeight);
  };

  const loadUpTranslations = useCallback(async () => {
    // If the locale is Valencian, we use the Spanish translations
    const effectiveLocale = locale === 'vlca' ? 'es' : locale;

    // Needed in order for media-chrome language support to work
    // https://www.media-chrome.org/docs/en/internationalization/adding-language-support#how-language-selection-works
    // This is currently commented out because the app is now also rewriting the
    // lang attribute on the html element and they conflict with media-chrome.
    // We are now calling the setLanguage function directly instead.
    // window.document.documentElement.setAttribute('lang', effectiveLocale);

    // media-chrome does not support German translations so we manually add them.
    // For fr and es we override the built-in translations with our custom versions.
    if (effectiveLocale === 'de') {
      addTranslation(effectiveLocale, Translations.de);
    } else if (effectiveLocale === 'fr') {
      addTranslation(effectiveLocale, Translations.fr);
    } else if (effectiveLocale === 'es') {
      addTranslation(effectiveLocale, Translations.es);
    }
  }, [locale]);

  const currentTrack = playlistTrack
    ? playQueue.current[playlistTrack.playlistIndex]?.data.tracks[
        playlistTrack.trackIndex
      ]
    : undefined;

  const hasTrackLevelAccess =
    currentTrack?.free_access !== undefined ||
    !!currentTrack?.subscription_tier ||
    !!currentTrack?.allowed_roles?.length;

  const acl = useCanAccess(
    (hasTrackLevelAccess
      ? currentTrack
      : playlistTrack
        ? playQueue.current[playlistTrack.playlistIndex]?.data
        : undefined) as unknown as AccessContentPayload,
  );

  useEffect(() => {
    loadUpTranslations();
  }, [loadUpTranslations]);

  useEffect(() => {
    // This sets up an observable for the body resizing and will adjust
    // the padding for the expanded player accordingly
    miniPlayerObserverRef.current = new ResizeObserver(handleResize);
    miniPlayerObserverRef.current.observe(document.body);

    setSiteConfigData(config);

    return () => miniPlayerObserverRef.current?.disconnect();
  }, [config]);

  useEffect(() => {
    const effectiveLocale = locale === 'vlca' ? 'es' : locale;

    setLanguage(effectiveLocale);
  }, [locale]);

  // This effect hook explicitly handles the zero state of the selected
  // format and gets it primed.  It should only fully run once.
  useEffect(() => {
    if (miniPlayerSelectedFormat) return;
    const format = getStreamingPreference() ?? 'mp3';
    setMiniPlayerSelectedFormat(format);
  }, [getStreamingPreference, miniPlayerSelectedFormat]);

  useEffect(() => {
    if (
      playlistTrack &&
      playQueue.current[playlistTrack.playlistIndex].data.type !==
        'personal_favourites' &&
      (acl === 'unauthenticated' || acl === 'unauthorized')
    ) {
      pendingLoadRef.current = null;

      // Save the blocked entry so we can resume it after the user logs in.
      blockedQueueEntryRef.current = {
        entry: playQueue.current[playlistTrack.playlistIndex],
        trackIndex: playlistTrack.trackIndex,
      };

      // Remove the blocked compilation from the queue so it doesn't
      // linger and affect next/previous navigation.
      playQueue.current.splice(playlistTrack.playlistIndex, 1);

      const previous = lastGrantedPlaylistTrackRef.current;
      let restoredTrack: PlaylistTrack | undefined;
      if (previous) {
        if (previous.playlistIndex < playlistTrack.playlistIndex) {
          restoredTrack = previous;
        } else if (previous.playlistIndex > playlistTrack.playlistIndex) {
          restoredTrack = {
            ...previous,
            playlistIndex: previous.playlistIndex - 1,
          };
        }
      }

      setPlaylistTrack(restoredTrack);

      if (!restoredTrack) {
        miniPlayerRef.current?.stop();
        miniPlayerRef.current?.load(undefined, undefined);
      }
      // When a restoredTrack exists, the free content is already playing in
      // the MiniPlayer — nothing stopped it — so no load() call is needed.

      openDialog('auth');
      return;
    }

    // Track the last position that was successfully granted so we can
    // restore it if the user tries to play blocked content.
    if (acl === 'granted' && playlistTrack) {
      lastGrantedPlaylistTrackRef.current = playlistTrack;
    }

    if (pendingLoadRef.current && acl !== 'loading') {
      const { compilation, track, format } = pendingLoadRef.current;
      pendingLoadRef.current = null;
      miniPlayerRef.current?.load({ compilation, track }, format);
    }

    setShowMiniPlayer(prev => {
      // If both playlistTrack and prev are undefined, we return undefined
      // because the mini player hasn't started playing yet
      if (playlistTrack === undefined && prev === undefined) return undefined;
      // If the playlistTrack is set and the previous value was false,
      // we return false to keep the mini player not shown
      if (playlistTrack !== undefined && prev === false) return false;

      // Else, we return true to show the mini player
      return playlistTrack !== undefined;
    });
  }, [playlistTrack, acl, openDialog]);

  const clearOutQueue = useCallback(() => {
    miniPlayerRef.current?.stop();
    // Clearing the final queue item can unmount the player before it emits its
    // pause event, so reset the shared playback state synchronously.
    setIsPlaying(false);
    setShowMiniPlayer(undefined);
    setShowExpandedPlayer(false);
    setPlaylistTrack(undefined);
    pendingLoadRef.current = null;
    lastGrantedPlaylistTrackRef.current = undefined;
    blockedQueueEntryRef.current = null;
    playQueue.current.splice(0, playQueue.current.length);
    miniPlayerRef.current?.load(undefined, undefined);
  }, []);

  const prevIsAuthenticatedRef = useRef(isAuthenticated);
  useEffect(() => {
    const wasAuthenticated = prevIsAuthenticatedRef.current;
    prevIsAuthenticatedRef.current = isAuthenticated;

    if (wasAuthenticated && !isAuthenticated) {
      clearOutQueue();
    }

    if (!wasAuthenticated && isAuthenticated && blockedQueueEntryRef.current) {
      const { entry, trackIndex } = blockedQueueEntryRef.current;
      blockedQueueEntryRef.current = null;

      playQueue.current.push(entry);
      const playlistIndex = playQueue.current.length - 1;

      setPlaylistTrack({ playlistIndex, trackIndex });
      setShowMiniPlayer(true);
      pendingLoadRef.current = {
        compilation: entry.data,
        track: entry.data.tracks[trackIndex],
        format: miniPlayerSelectedFormat,
      };
    }
  }, [isAuthenticated, miniPlayerSelectedFormat, clearOutQueue]);

  // This effect is used to force a check on the mini player height
  // when the showMiniPlayer state changes.
  useEffect(handleResize, [showMiniPlayer]);
  useEffect(() => {
    // Set the padding bottom of the body to the height of the mini player
    document.body.style.paddingBottom = `${miniPlayerHeight ?? 0}px`;
  });

  const queueUpCompilation = useCallback<QueueUpCompilationFunction>(
    (key, compilation, startTrackIndex) => {
      const data = normalizeCompilation(compilation);
      const existingIndex = playQueue.current.findIndex(
        item => item.key === key,
      );
      // If the key is set, then we update the playlist
      if (existingIndex !== -1) {
        playQueue.current[existingIndex].data = data;
      }
      // Else, we add a new entry to the play queue
      else {
        playQueue.current.push({ key, data });
      }
      // If the startTrackIndex is set, then the user wants to play a track
      // from the playlist
      if (startTrackIndex !== undefined && startTrackIndex >= 0) {
        const playlistIndex =
          existingIndex !== -1 ? existingIndex : playQueue.current.length - 1;
        const trackIndex = startTrackIndex;
        const track = playQueue.current[playlistIndex].data.tracks[trackIndex];

        if (!track) {
          logger.error('Track not found at index', {
            source: 'nextjs-app-media-controller',
            trackIndex,
            tracksCount: data.tracks.length,
          });
          return;
        }

        setPlaylistTrack({ playlistIndex, trackIndex });
        setShowMiniPlayer(true);

        pendingLoadRef.current = {
          compilation: playQueue.current[playlistIndex].data,
          track,
          format: miniPlayerSelectedFormat,
        };
      }
    },
    [miniPlayerSelectedFormat],
  );

  const pauseMiniPlayer = useCallback(
    (hideMiniPlayer = false) => {
      if (playlistTrack === undefined) return;

      miniPlayerRef.current?.pause();

      if (hideMiniPlayer) {
        setShowMiniPlayer(false);
      }
    },
    [playlistTrack],
  );

  const resumeMiniPlayer = useCallback(
    (showMiniPlayer = false) => {
      if (playlistTrack === undefined) return;

      miniPlayerExplicityPaused.current = false;
      miniPlayerRef.current?.play();

      if (showMiniPlayer) {
        setShowMiniPlayer(true);
      }
    },
    [playlistTrack],
  );

  const handleOnMiniPlayerEnded = () => handleOnPlayerNext();
  const handleOnPlayerDuration = (duration: Duration) =>
    setMiniPlayerDuration(duration);

  const handleOnPlayerPrevious = () => {
    if (!playlistTrack) return;

    if (acl === 'loading') return;

    const hasPreviousPlaylist = playlistTrack.playlistIndex > 0;
    const hasPreviousTrackInPlaylist = playlistTrack.trackIndex > 0;

    if (hasPreviousTrackInPlaylist) {
      const playlistIndex = playlistTrack.playlistIndex;
      const trackIndex = playlistTrack.trackIndex - 1;
      setPlaylistTrack({ playlistIndex, trackIndex });

      miniPlayerRef.current?.load(
        {
          compilation: playQueue.current[playlistIndex].data,
          track: playQueue.current[playlistIndex].data.tracks[trackIndex],
        },
        miniPlayerSelectedFormat,
      );
    } else if (hasPreviousPlaylist) {
      const playlistIndex = playlistTrack.playlistIndex - 1;
      const trackIndex =
        playQueue.current[playlistIndex].data.tracks.length - 1;
      setPlaylistTrack({ playlistIndex, trackIndex });

      miniPlayerRef.current?.load(
        {
          compilation: playQueue.current[playlistIndex].data,
          track: playQueue.current[playlistIndex].data.tracks[trackIndex],
        },
        miniPlayerSelectedFormat,
      );
    }
  };

  const handleOnPlayerNext = useCallback(() => {
    if (!playlistTrack) return;

    const hasNextPlaylist =
      playlistTrack.playlistIndex < playQueue.current.length - 1;
    const hasNextTrackInPlaylist =
      playlistTrack.trackIndex <
      playQueue.current[playlistTrack.playlistIndex].data.tracks.length - 1;

    // The currently playing playlist has another track
    if (hasNextTrackInPlaylist) {
      const playlistIndex = playlistTrack.playlistIndex;
      const trackIndex = playlistTrack.trackIndex + 1;
      setPlaylistTrack({ playlistIndex, trackIndex });

      miniPlayerRef.current?.load(
        {
          compilation: playQueue.current[playlistIndex].data,
          track: playQueue.current[playlistIndex].data.tracks[trackIndex],
        },
        miniPlayerSelectedFormat,
      );
    }
    // The play queue has another playlist
    else if (hasNextPlaylist) {
      const playlistIndex = playlistTrack.playlistIndex + 1;
      const trackIndex = 0;
      setPlaylistTrack({ playlistIndex, trackIndex });

      miniPlayerRef.current?.load(
        {
          compilation: playQueue.current[playlistIndex].data,
          track: playQueue.current[playlistIndex].data.tracks[trackIndex],
        },
        miniPlayerSelectedFormat,
      );
    }
    // We've hit the end of the play queue
    else {
      clearOutQueue();
    }
  }, [playlistTrack, miniPlayerSelectedFormat, clearOutQueue]);

  const handleOnInfoClick = () => {
    setShowExpandedPlayer(true);
    setShowMiniPlayer(false);
  };

  const handleOnInfoCloseClick = () => {
    setShowExpandedPlayer(false);
    setShowMiniPlayer(true);
  };

  const handleOnFormatSelect = (format: TrackFileType) => {
    setMiniPlayerSelectedFormat(format);
    setStreamingPreference(format);

    if (!playlistTrack) return;
    const { playlistIndex, trackIndex } = playlistTrack;

    miniPlayerRef.current?.load(
      {
        compilation: playQueue.current[playlistIndex].data,
        track: playQueue.current[playlistIndex].data.tracks[trackIndex],
      },
      format,
    );
  };

  const handleOnPlayerError = useCallback(
    (error: Error) => {
      // TODO: Send error to Sentry
      toastRef.current?.open({
        title: 'Unexpected error',
        message: error.message,
      });

      handleOnPlayerNext();
    },
    [handleOnPlayerNext],
  );

  const getTrackMetadata = useCallback(
    async (
      compilation: CompilationNormalized,
      track: TrackNormalized,
      format: TrackFileType,
    ) => {
      if (!format) return;
      const { id, type } = compilation;
      const { trackId } = track;

      try {
        const asset = await normalizeAsset(
          config.id,
          id,
          type,
          trackId,
          format,
        );

        if (!asset) {
          logger.error('Error fetching asset', {
            source: 'nextjs-app-media-controller',
          });
          return;
        }

        setMiniPlayerAvailableFormats(asset.availableTypes);

        return asset;
      } catch (error) {
        handleOnPlayerError(error as Error);
      }
    },
    [config.id, handleOnPlayerError],
  );

  const disableMiniPlayerPrevious =
    playlistTrack?.playlistIndex === 0 && playlistTrack.trackIndex === 0;
  const currentCompilationId = playlistTrack
    ? (playQueue.current[playlistTrack.playlistIndex]?.data.id as UUID)
    : undefined;

  const currentCompilationType = playlistTrack
    ? playQueue.current[playlistTrack.playlistIndex]?.data.type
    : undefined;

  const currentTrackId = playlistTrack
    ? playQueue.current[playlistTrack.playlistIndex]?.data.tracks[
        playlistTrack.trackIndex
      ]?.trackId
    : undefined;
  const trackIndex = playlistTrack?.trackIndex;

  const contextValue = useMemo(
    () => ({
      queueUpCompilation,
      clearOutQueue,
      pauseMiniPlayer,
      resumeMiniPlayer,
      currentCompilationId,
      isPlaying,
      currentCompilationType,
      currentTrackId,
      trackIndex,
      openAddMenu: (trackId: number) => {
        setAddMenuTrackId(trackId);
        setShowExpandedPlayer(false);
        setShowMiniPlayer(true);
      },
      closeAddMenu: () => setAddMenuTrackId(null),
    }),
    [
      queueUpCompilation,
      clearOutQueue,
      pauseMiniPlayer,
      resumeMiniPlayer,
      currentCompilationId,
      isPlaying,
      currentCompilationType,
      currentTrackId,
      trackIndex,
    ],
  );
  return (
    <MediaControllerContext.Provider value={contextValue}>
      <div>{children}</div>
      {/* TODO: We should not make onOpenChange required */}
      <Sheet.Root
        className="flex flex-col h-dvh"
        isOpen={showExpandedPlayer}
        onOpenChange={() => {}}
      >
        <Sheet.Header>
          {/* TODO: Sheet.Title and Sheet.Description need to be wrapped with @radix-ui/react-visually-hidden */}
          <Sheet.Title className="hidden">Expanded Player</Sheet.Title>
          <Sheet.Description className="hidden">
            Expanded player experience
          </Sheet.Description>
          <div className="flex flex-1 justify-end">
            <Button variant="ghost" onClick={handleOnInfoCloseClick}>
              <HeroIcon icon={XMarkIcon} />
            </Button>
          </div>
        </Sheet.Header>
        <Sheet.Body className="overflow-y-scroll">
          <div>
            <ExpandedPlayer
              compilation={
                playlistTrack &&
                playQueue.current[playlistTrack.playlistIndex]?.data
              }
              track={
                playlistTrack
                  ? playQueue.current[playlistTrack.playlistIndex]?.data.tracks[
                      playlistTrack.trackIndex
                    ]
                  : undefined
              }
              duration={miniPlayerDuration}
              preferredFormat={miniPlayerSelectedFormat}
              availableFormats={miniPlayerAvailableFormats}
              image={
                playlistTrack
                  ? (playQueue.current[playlistTrack.playlistIndex]?.data
                      .tracks[playlistTrack.trackIndex]?.image ?? undefined)
                  : undefined
              }
              disablePrevious={disableMiniPlayerPrevious}
              onPauseExplicit={() => (miniPlayerExplicityPaused.current = true)}
              onFormatSelect={handleOnFormatSelect}
              onPrevious={handleOnPlayerPrevious}
              onNext={handleOnPlayerNext}
            />
          </div>
        </Sheet.Body>
      </Sheet.Root>
      <div
        ref={miniPlayerWrapperRef}
        className={clsx({
          hidden: !showMiniPlayer,
          'fixed left-0 right-0 bottom-0 z-[51]': true,
        })}
      >
        <MiniPlayer
          ref={miniPlayerRef}
          tenantId={siteConfigData?.configuration?.tenant_id}
          muxDataId={siteConfigData?.configuration?.analytics?.mux}
          disablePrevious={disableMiniPlayerPrevious}
          getTrackMetadata={getTrackMetadata}
          availableFormats={miniPlayerAvailableFormats}
          preferredFormat={miniPlayerSelectedFormat}
          onDurationChange={handleOnPlayerDuration}
          onFormatSelect={handleOnFormatSelect}
          onPlaying={() => {
            setIsPlaying(true);
            miniPlayerExplicityPaused.current = false;
          }}
          onPause={() => setIsPlaying(false)}
          onPauseExplicit={() => (miniPlayerExplicityPaused.current = true)}
          onInfoClick={handleOnInfoClick}
          onEnded={handleOnMiniPlayerEnded}
          onPrevious={handleOnPlayerPrevious}
          onNext={handleOnPlayerNext}
          onError={handleOnPlayerError}
        />
      </div>
      <Toast
        ref={toastRef}
        className="fixed bottom-0 right-0 z-[100]"
        offsetY={miniPlayerHeight}
      />
      {addMenuTrackId !== null && (
        <AddMenu
          drawerOpen={true}
          drawerOpenAction={() => setAddMenuTrackId(null)}
          trackId={addMenuTrackId}
        />
      )}
    </MediaControllerContext.Provider>
  );
}
